import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  getAccountingAccess,
  getAccountingActivity,
  getAccountingChecklist,
  getAccountingDocumentRequests,
  getAccountingProfile,
} from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import SalonBusinessCpaAccessRequired from "./SalonBusinessCpaAccessRequired";
import { AccountingProfileForm } from "./SalonBusinessCpaProfileIntake";
import SalonBusinessCpaShell, { AccountingCard } from "./SalonBusinessCpaShell";
import {
  flattenChecklistTasks,
  getTaskActionPath,
  openTaskStatuses,
  readyTaskStatuses,
  taskStatusClasses,
} from "./accountingTasks";

const cards = [
  {
    title: "Documents to upload",
    body: "Keep tax slips, bank statements, payroll records, and receipts organized in one place.",
    to: "/salonbusinesscpa/documents",
  },
  {
    title: "Accounting source data",
    body: "Review sales, tax collected, payment methods, and staff payroll activity from Ellyanna Booking.",
    to: "/salonbusinesscpa/source-data",
  },
  {
    title: "GST/HST checklist",
    body: "Track sales tax setup, filing dates, and the records your accountant needs.",
    to: "/salonbusinesscpa/gst-hst",
    moduleKey: "needs_gst_hst",
  },
  {
    title: "Payroll checklist",
    body: "Prepare payroll remittance details, pay stub records, and staff payment notes.",
    to: "/salonbusinesscpa/payroll",
    moduleKey: "needs_payroll",
  },
  {
    title: "Bookkeeping checklist",
    body: "Review monthly sales, expenses, payment deposits, and cleanup items.",
    to: "/salonbusinesscpa/bookkeeping",
    moduleKey: "needs_bookkeeping",
  },
  {
    title: "Corporate tax checklist",
    body: "Organize year-end company records, shareholder notes, and filing details.",
    to: "/salonbusinesscpa/corporate-tax",
    moduleKey: "needs_corporate_tax",
  },
  {
    title: "Personal tax checklist",
    body: "Gather owner and independent professional records for personal tax filing.",
    to: "/salonbusinesscpa/personal-tax",
    moduleKey: "needs_personal_tax",
  },
  {
    title: "Financial statements",
    body: "Prepare clean records for income statements, balance sheets, and owner review.",
    to: "/salonbusinesscpa/financial-statements",
    moduleKey: "needs_financial_statements",
  },
  {
    title: "Messages from accountant",
    body: "Accountant messaging will appear here as the portal grows.",
    to: "/salonbusinesscpa/documents",
  },
];

function statusLabel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "Checking";
  if (normalized === "test_free") return "Testing account";
  if (normalized === "no_card_on_file") return "No card on file";
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function packagePriceLabel(cents) {
  const value = Number(cents || 0);
  if (!value) return "$0/month";
  return `$${Math.round(value / 100)}/month`;
}

export default function SalonBusinessCpaDashboard() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [profile, setProfile] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [requests, setRequests] = useState([]);
  const [activity, setActivity] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    let active = true;
    setLoading(true);
    setError("");
    getAccountingAccess(token)
      .then(async (accessData) => {
        if (!active) return;
        setAccess(accessData);
        if (!accessData?.eligible) return;
        const [profileData, checklistData, requestData, activityData] = await Promise.all([
          getAccountingProfile(token).catch(() => null),
          getAccountingChecklist(token).catch(() => null),
          getAccountingDocumentRequests(token).catch(() => null),
          getAccountingActivity(token, { limit: 8 }).catch(() => null),
        ]);
        if (!active) return;
        setProfile(profileData);
        setChecklist(checklistData);
        setRequests(Array.isArray(requestData?.requests) ? requestData.requests : []);
        setActivity(Array.isArray(activityData?.events) ? activityData.events : []);
      })
      .catch((err) => {
        if (active) {
          setError(
            err?.response?.data?.detail || "Unable to load SalonBusinessCPA access."
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  const checklistCount = useMemo(() => {
    return flattenChecklistTasks(checklist).length;
  }, [checklist]);

  const taskSummary = useMemo(() => {
    const tasks = flattenChecklistTasks(checklist);
    const openTasks = tasks.filter((task) => openTaskStatuses.has(task.status));
    const readyTasks = tasks.filter((task) => readyTaskStatuses.has(task.status));
    const nextTasks = openTasks
      .filter((task) => task.document_type)
      .concat(openTasks.filter((task) => !task.document_type))
      .slice(0, 4);
    return { openTasks, readyTasks, nextTasks };
  }, [checklist]);

  const visibleCards = useMemo(() => {
    const flags = profile?.module_flags || {};
    if (!profile?.onboarding_completed) {
      return cards.filter((card) => !card.moduleKey);
    }
    return cards.filter((card) => !card.moduleKey || Boolean(flags[card.moduleKey]));
  }, [profile]);

  const openRequests = useMemo(
    () => requests.filter((request) => !["done", "cancelled"].includes(request.status)),
    [requests]
  );

  const accountingStatus = useMemo(() => {
    const accessStatus = String(profile?.accounting_access_status || access?.accounting_access_status || "").toLowerCase();
    const currentPackageCode =
      profile?.current_accounting_package_code || access?.accounting_package_code || null;
    const recommended = profile?.recommended_package || null;
    const currentPrice =
      Number(profile?.current_accounting_package_price_cents || access?.accounting_package_price_cents || 0);
    const mismatch =
      Boolean(currentPackageCode && recommended?.code && currentPackageCode !== recommended.code) ||
      Boolean(currentPackageCode && recommended?.custom_quote);
    if (!profile?.accounting_services_enabled && !profile?.onboarding_completed && !currentPackageCode) {
      return {
        label: "Not started",
        tone: "slate",
        body: "Turn on SalonBusinessCPA in Ellyanna setup when you want accounting, tax, payroll, or bookkeeping support.",
        actionLabel: "Open Ellyanna setup",
        actionHref: "https://ellyanna.com/setup",
      };
    }
    if (!profile?.onboarding_completed) {
      return {
        label: "Setup needed",
        tone: "amber",
        body: "Answer a few questions so SalonBusinessCPA can show the right checklist and recommended package.",
        actionLabel: "Finish setup",
        actionTo: "/salonbusinesscpa/profile",
      };
    }
    if (mismatch) {
      return {
        label: "Package review needed",
        tone: "amber",
        body: "Your selected accounting package may not match your current SalonBusinessCPA setup.",
        actionLabel: "Review billing",
        actionHref: "https://ellyanna.com/manager/manage/billing",
      };
    }
    if (currentPackageCode) {
      return {
        label: "Package selected",
        tone: "emerald",
        body: `Your SalonBusinessCPA package is active at ${packagePriceLabel(currentPrice)}.`,
        actionLabel: "View billing",
        actionHref: "https://ellyanna.com/manager/manage/billing",
      };
    }
    if (accessStatus === "trialing") {
      const days = Number(profile?.accounting_trial_days_remaining || access?.accounting_trial_days_remaining || 0);
      return {
        label: `${days} trial day${days === 1 ? "" : "s"} left`,
        tone: "sky",
        body: "Your accounting trial is active. Confirm a paid package in billing to keep using SalonBusinessCPA after the trial.",
        actionLabel: "Confirm package",
        actionHref: "https://ellyanna.com/manager/manage/billing",
      };
    }
    if (accessStatus === "trial_expired") {
      return {
        label: "Trial ended",
        tone: "rose",
        body: "Choose a paid SalonBusinessCPA package in billing to continue using the accounting portal.",
        actionLabel: "Open billing",
        actionHref: "https://ellyanna.com/manager/manage/billing",
      };
    }
    return {
      label: "Needs billing confirmation",
      tone: "amber",
      body: recommended?.custom_quote
        ? "Your setup needs a custom SalonBusinessCPA review before choosing a package."
        : "Confirm the recommended SalonBusinessCPA package in billing.",
      actionLabel: "Open billing",
      actionHref: "https://ellyanna.com/manager/manage/billing",
    };
  }, [access, profile]);

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#f8f6f4] text-sm text-slate-500">
        Loading SalonBusinessCPA...
      </main>
    );
  }

  if (error) {
    return (
      <SalonBusinessCpaShell
        title="SalonBusinessCPA"
        description="We could not load your accounting portal access right now."
      >
        <div className="rounded-3xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
          {error}
        </div>
      </SalonBusinessCpaShell>
    );
  }

  if (!access?.eligible) {
    return <SalonBusinessCpaAccessRequired access={access} />;
  }

  return (
    <SalonBusinessCpaShell
      title="SalonBusinessCPA Dashboard"
      description="Organize accounting, tax, payroll, bookkeeping, GST/HST, and year-end items for your salon."
      actions={
        <>
          <Link
            to="/salonbusinesscpa/documents"
            className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600"
          >
            Upload documents
          </Link>
          <Link
            to="/salonbusinesscpa/profile"
            className="rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Setup
          </Link>
        </>
      }
    >
      {!profile?.onboarding_completed ? (
        <section className="rounded-3xl border border-amber-200 bg-amber-50 p-5">
          <h2 className="text-lg font-semibold text-slate-950">
            Start with your accounting setup
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-700">
            Choose how you work, confirm your staff range when needed, and see
            the SalonBusinessCPA package that best fits your accounting, tax,
            payroll, bookkeeping, and record needs.
          </p>
          <div className="mt-5">
            <AccountingProfileForm
              profile={profile}
              onSaved={async (savedProfile) => {
                setProfile(savedProfile);
                const checklistData = await getAccountingChecklist(token).catch(() => null);
                setChecklist(checklistData);
              }}
            />
          </div>
        </section>
      ) : null}

      <section
        className={`rounded-3xl border p-6 shadow-sm ${
          accountingStatus.tone === "emerald"
            ? "border-emerald-200 bg-emerald-50"
            : accountingStatus.tone === "sky"
            ? "border-sky-200 bg-sky-50"
            : accountingStatus.tone === "rose"
            ? "border-rose-200 bg-rose-50"
            : accountingStatus.tone === "amber"
            ? "border-amber-200 bg-amber-50"
            : "border-slate-200 bg-white"
        }`}
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              SalonBusinessCPA status
            </p>
            <h2 className="mt-2 text-lg font-semibold text-slate-950">
              {accountingStatus.label}
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-700">
              {accountingStatus.body}
            </p>
          </div>
          {accountingStatus.actionTo ? (
            <Link
              to={accountingStatus.actionTo}
              className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-full bg-rose-500 px-4 text-sm font-semibold text-white hover:bg-rose-600"
            >
              {accountingStatus.actionLabel}
            </Link>
          ) : accountingStatus.actionHref ? (
            <a
              href={accountingStatus.actionHref}
              className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-full bg-rose-500 px-4 text-sm font-semibold text-white hover:bg-rose-600"
            >
              {accountingStatus.actionLabel}
            </a>
          ) : null}
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <AccountingCard title="Access" body="Your portal is connected to your Ellyanna Booking subscription.">
          <p className="text-2xl font-semibold text-slate-950">
            {statusLabel(access.subscription_status)}
          </p>
        </AccountingCard>
        <AccountingCard title="Business" body="Current business context for accounting support.">
          <p className="text-2xl font-semibold text-slate-950">
            {profile?.business_name || user?.business_name || "Your salon"}
          </p>
        </AccountingCard>
        <AccountingCard title="Setup" body="The portal uses this to show the right accounting steps.">
          <p className="text-2xl font-semibold text-slate-950">
            {profile?.client_profile_label || "Not set"}
          </p>
        </AccountingCard>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <AccountingCard title="Checklist items" body="Tasks prepared for your current profile.">
          <p className="text-2xl font-semibold text-slate-950">{checklistCount || 0}</p>
        </AccountingCard>
        <AccountingCard title="Records received" body="Uploaded records already received or in review.">
          <p className="text-2xl font-semibold text-slate-950">
            {taskSummary.readyTasks.length || 0}
          </p>
        </AccountingCard>
        <AccountingCard title="Open items" body="Records or setup notes still needed.">
          <p className="text-2xl font-semibold text-slate-950">
            {taskSummary.openTasks.length || 0}
          </p>
        </AccountingCard>
      </section>

      {profile?.onboarding_completed && openRequests.length ? (
        <section className="rounded-3xl border border-amber-200 bg-amber-50 p-6">
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">
                Requests from your accountant
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-700">
                These records were requested for your accounting or tax review.
                Upload them first so the review can keep moving.
              </p>
            </div>
            <Link
              to="/salonbusinesscpa/requests"
              className="rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600"
            >
              View requests
            </Link>
          </div>
          <div className="mt-5 grid gap-3 lg:grid-cols-2">
            {openRequests.slice(0, 4).map((request) => {
              const params = new URLSearchParams({
                document_type: request.document_type,
                request_id: String(request.id),
              });
              return (
                <article
                  key={request.id}
                  className="rounded-2xl border border-amber-200 bg-white p-4"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-950">
                        {request.title}
                      </h3>
                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        {request.client_note ||
                          "Please upload this record so your review can continue."}
                      </p>
                    </div>
                    <Link
                      to={`/salonbusinesscpa/documents?${params.toString()}`}
                      className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-full bg-rose-500 px-4 text-sm font-semibold text-white hover:bg-rose-600"
                    >
                      Upload
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ) : null}

      {profile?.onboarding_completed ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">
                What to do next
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Start with these records so accounting, tax, payroll, and GST/HST
                review can move faster.
              </p>
            </div>
            <Link
              to="/salonbusinesscpa/checklist"
              className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              View all tasks
            </Link>
          </div>
          <div className="mt-5 grid gap-3 lg:grid-cols-2">
            {taskSummary.nextTasks.length ? (
              taskSummary.nextTasks.map((task) => (
                <article
                  key={task.code}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-slate-950">
                          {task.title}
                        </h3>
                        <span
                          className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
                            taskStatusClasses[task.status] ||
                            "border-slate-200 bg-slate-100 text-slate-700"
                          }`}
                        >
                          {task.status_label || "Needed"}
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        {task.body}
                      </p>
                    </div>
                    <Link
                      to={getTaskActionPath(task)}
                      className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-full bg-rose-500 px-4 text-sm font-semibold text-white hover:bg-rose-600"
                    >
                      {task.action_label || "Upload document"}
                    </Link>
                  </div>
                </article>
              ))
            ) : (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
                No urgent records are showing right now. Check the full checklist
                before your next review period.
              </div>
            )}
          </div>
        </section>
      ) : null}

      {profile?.onboarding_completed && activity.length ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">
                Recent activity
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                See recent document requests, uploads, and review updates in one place.
              </p>
            </div>
            <Link
              to="/salonbusinesscpa/requests"
              className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Open requests
            </Link>
          </div>
          <ol className="mt-5 grid gap-3 lg:grid-cols-2">
            {activity.slice(0, 6).map((event) => (
              <li
                key={event.id}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
              >
                <div className="flex gap-3">
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-rose-500" />
                  <div>
                    <h3 className="text-sm font-semibold text-slate-950">
                      {event.title}
                    </h3>
                    {event.body ? (
                      <p className="mt-1 text-sm leading-6 text-slate-600">
                        {event.body}
                      </p>
                    ) : null}
                    <p className="mt-1 text-xs text-slate-500">
                      {formatActivityDate(event.occurred_at_utc)}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {visibleCards.map((card) => (
          <AccountingCard key={card.title} {...card} />
        ))}
      </section>
    </SalonBusinessCpaShell>
  );
}

function formatActivityDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}
